import React, { useState, useMemo, useRef, useEffect } from "react";
import "react-day-picker/dist/style.css";
import {
  sortedData,
  formatApptDate,
  formatStatusBadge,
  formatMobileNumber,
  formatApptDuration,
  FormatFollowUpStatus,
  FormatCallStatusDetails,
} from "./PageFormats";
import PageTableStatusFilter from "./PageTableStatusFilter";

export default function PageTable({
  TableData,
  TableColumns,
  onFetchFollowUp,
}) {
  const statusFilterKeys = useMemo(
    () => ({
      callStatFilterKey: "CallStatus",
      followUpStatFilterKey: "FollowUp",
      apptStatFilterKey: "PatientStatus",
    }),
    []
  );

  const uniqueApptStatOpts = useMemo(() => {
    const getUniqueNormalizedValues = (key) => {
      const values = TableData?.map((item) => item?.[key]) || [];
      return Array.from(
        new Set(
          values
            .filter(Boolean)
            .map((val) => val)
            .map((val) => val)
        )
      );
    };

    return {
      [statusFilterKeys.apptStatFilterKey]: getUniqueNormalizedValues(
        statusFilterKeys.apptStatFilterKey
      ),
      [statusFilterKeys.callStatFilterKey]: getUniqueNormalizedValues(
        statusFilterKeys.callStatFilterKey
      ),
      [statusFilterKeys.followUpStatFilterKey]: [
        "Pending",
        "In Process",
        "Completed",
      ],
    };
  }, [TableData, statusFilterKeys]);

  const [statusFilters, setStatusFilters] = useState(() => ({
    [statusFilterKeys.apptStatFilterKey]: new Set(), // empty Set = “All”
    [statusFilterKeys.callStatFilterKey]: new Set(),
    [statusFilterKeys.followUpStatFilterKey]: new Set(),
  }));
  const audioRef = useRef(null);
  const [audioPath, setAudioPath] = useState(null);
  const [playingRowId, setPlayingRowId] = useState(null);

  const handlePlayPause = (path, rowId) => {
    if (!audioRef.current) return;

    if (audioPath === path && playingRowId === rowId) {
      audioRef.current.pause();
      setPlayingRowId(null);
      setAudioPath(null);
      return;
    }

    document.querySelectorAll("audio").forEach((audioEl) => {
      if (audioEl !== audioRef.current && !audioEl.paused) {
        audioEl.pause();
      }
    });

    setAudioPath(path);
    setPlayingRowId(rowId);
    setTimeout(() => {
      audioRef.current.play().catch((err) => {
        console.error("Audio play error:", err);
        setPlayingRowId(null);
        setAudioPath(null);
      });
    }, 0);
  };

  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl) return;
    const onEnded = () => {
      setPlayingRowId(null);
      setAudioPath(null);
    };
    const onPaused = () => {
      setPlayingRowId(null);
      setAudioPath(null);
    };
    audioEl.addEventListener("ended", onEnded);
    audioEl.addEventListener("pause", onPaused);
    return () => {
      audioEl.removeEventListener("ended", onEnded);
      audioEl.removeEventListener("pause", onPaused);
    };
  }, []);

  const filteredTableData = useMemo(() => {
    if (!TableData?.length) return [];
    return sortedData(
      TableData.filter((appt) => {
        return Object.entries(statusFilters).every(([key, set]) => {
          // empty Set ➜ no restriction (i.e. “All”)
          return set.size === 0 || set.has(appt?.[key]);
        });
      })
    );
  }, [TableData, statusFilters]);

  // Calculate row count and decide scroll style
  const rowCount = filteredTableData.length;
  const shouldScroll = rowCount >= 5;

  const dataFormatRequiredCols = {
    PatientMobile: formatMobileNumber,
    AppointmentDate: (val) => formatApptDate(val),
    PatientStatus: formatStatusBadge,
    AppointmentDuration: formatApptDuration,
    CallStatus: formatStatusBadge,
    FollowUp: (val, row) => (
      <FormatFollowUpStatus
        value={val}
        followUpPatient={row}
        onFetchFollowUp={onFetchFollowUp}
      />
    ),
    AgentOverallStatus: (val, row) => (
      <FormatCallStatusDetails
        appointment={row}
        pageName="Escalations"
        isPlaying={playingRowId === row?.PatientID}
        handlePlayPause={() =>
          handlePlayPause(row?.Recordingpath, row?.PatientID)
        }
      />
    ),
  };

  return TableColumns && TableData ? (
    <>
      <div className="w-full overflow-x-auto">
        <audio ref={audioRef} src={audioPath} preload="none" />
        <div className="w-full">
          <table className="w-full min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-100 xl:sticky xl:top-0 xl:z-10">
              <tr>
                {TableColumns.map((column) => {
                  const key = Object.keys(column)[0];
                  const label = column[key];
                  const centerAlign = column?.columnAlign ? "center" : "left";
                  const columnFixedWidth = column?.columnWidth || "w-[120px]";
                  return (
                    <th
                      key={key}
                      scope="col"
                      className={`px-2 py-3 text-sm text-gray-900 hover:bg-gray-100 text-${centerAlign} whitespace-nowrap ${columnFixedWidth}`}
                    >
                      <div
                        className={`flex flex-col items-${centerAlign} justify-${centerAlign} ${columnFixedWidth}`}
                      >
                        <span className="mb-1 truncate">{label}</span>
                        {Object.values(statusFilterKeys).includes(key) && (
                          <div className="min-w-[80px]">
                            <PageTableStatusFilter
                              options={uniqueApptStatOpts?.[key] || []}
                              selectedSet={statusFilters[key]}
                              onChange={(newSet) =>
                                setStatusFilters((prev) => ({
                                  ...prev,
                                  [key]: newSet,
                                }))
                              }
                            />
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
          </table>
          {/* Scrollable container for body */}
          <div
            className={`w-full ${
              shouldScroll ? "xl:max-h-[360px] xl:overflow-y-auto" : ""
            }`}
          >
            <table className="w-full min-w-full divide-y divide-gray-200 table-fixed">
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTableData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={TableColumns.length}
                      className="px-4 py-4 text-sm text-center text-gray-900"
                    >
                      No Appointments Found Matching the Criteria.
                    </td>
                  </tr>
                ) : (
                  filteredTableData.map((row, rowIdx) => (
                    <tr
                      key={row?.appointmentId || row?.PatientID || rowIdx}
                      className="transition-colors hover:bg-gray-200"
                    >
                      {TableColumns.map((col, colIdx) => {
                        const key = Object.keys(col)[0];
                        const formatFnMap = dataFormatRequiredCols;
                        const centerAlign = col?.columnAlign
                          ? "center"
                          : "left";
                        const columnFixedWidth =
                          col?.columnWidth || "w-[120px]";
                        const hasKey = Object.prototype.hasOwnProperty.call(
                          row,
                          key
                        );
                        let value = hasKey ? row[key] : undefined;
                        value =
                          hasKey && formatFnMap[key] && value
                            ? formatFnMap[key](value, row)
                            : hasKey
                            ? value ?? "-"
                            : "-";
                        return (
                          <td
                            key={colIdx}
                            className={`px-4 py-3 text-sm text-${centerAlign} text-gray-900 whitespace-nowrap ${columnFixedWidth}`}
                          >
                            <div
                              className={`flex items-${centerAlign} justify-${centerAlign} ${columnFixedWidth}`}
                            >
                              <span className="truncate">{value}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  ) : null;
}
